import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import './SignUp.css';
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop';

const SignupPage = () => {
    const { setUser } = useContext(UserContext); // Access setUser from context

    // State to handle form inputs
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
    });

    const navigate = useNavigate();

    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [apiErrorMessage, setApiErrorMessage] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        // setFormData({ ...formData, [name]: value });
        if (name === "phoneNumber") {
            // Remove non-numeric characters
            const numericValue = value.replace(/\D/g, "").slice(0, 10); // Limit to 10 digits
            setFormData({ ...formData, phoneNumber: numericValue });
        } else if (name === "email") {
            setFormData({ ...formData, email: value.toLowerCase() });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.username.trim()) newErrors.username = 'Username is required';
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email address is invalid';
        }
        if (!formData.firstName) {
            newErrors.firstName = 'First Name is required';
        }
        if (!formData.lastName) {
            newErrors.lastName = 'Last Name is required';
        }
        if (!formData.phoneNumber) {
            newErrors.phoneNumber = 'Phone Number is required';
        }
        if (!/^\d{10}$/.test(formData.phoneNumber)) {
            newErrors.phoneNumber = "Phone number must be exactly 10 digits";
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8 || !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W)/.test(formData.password)) {
            newErrors.password = 'Password must be at least 8 characters \nPassword must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Confirm Password is required';
        } else {
            if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            try {
                const response = await fetch('https://3nrqnhje08.execute-api.us-east-2.amazonaws.com/api/Users', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        email: formData.email,
                        phoneNumber: formData.phoneNumber,
                        username: formData.username,
                        password: formData.password,
                    }),
                });

                const user = await response.json();

                if (!response.ok || user.errorMessage) {
                    const newErrorsPostApiCall = {};

                    if (user.errorMessage === "Email already in use") {
                        newErrorsPostApiCall.email = user.errorMessage;
                    } else if (user.errorMessage === "Username already in use") {
                        newErrorsPostApiCall.username = user.errorMessage;
                    } else if (user.errorMessage === "Password must be 8 characters long and contain one of each (Capital Letter, Number, Special Character)") {
                        newErrorsPostApiCall.password = user.errorMessage;
                    } else {
                        setApiErrorMessage(user.errorMessage || 'An error occurred. Please try again.');
                    }

                    setErrors(newErrorsPostApiCall);
                    return;
                }

                setFormData({
                    username: '',
                    email: '',
                    firstName: '',
                    lastName: '',
                    phoneNumber: '',
                    password: '',
                    confirmPassword: ''
                });
                setErrors({});
                setApiErrorMessage('');
                navigate('/login', {
                    state: {
                        verifyEmailMesage: `A confirmation email has been sent to ${formData.email}. Please check your inbox to verify your email address.`
                    }
                });
            } catch (err) {
                setApiErrorMessage('An unexpected error occurred. Please try again.');
                console.error(err);
            }
        }
    };

    return (
        <div className="sign-up-vla-container">
            <ScrollToTop />
            <h1>Sign Up</h1>
            <form onSubmit={handleSubmit}>
                <p className="required-note">Fields marked with <span className="asterisk">*</span> are required.</p>
                <div className="sign-up-form-group">
                    <label>Username<span className="asterisk">*</span></label>
                    <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        className={errors.username ? "error" : ""}
                    />
                    {errors.username && <p className="sign-up-error">{errors.username}</p>}
                </div>
                <div className="sign-up-form-group">
                    <label>Email<span className="asterisk">*</span></label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={errors.email ? "error" : ""}
                    />
                    {errors.email && <p className="sign-up-error">{errors.email}</p>}
                </div>
                <div className="sign-up-form-group">
                    <label>First Name<span className="asterisk">*</span></label>
                    <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={errors.firstName ? "error" : ""}
                    />
                    {errors.firstName && <p className="sign-up-error">{errors.firstName}</p>}
                </div>
                <div className="sign-up-form-group">
                    <label>Last Name<span className="asterisk">*</span></label>
                    <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={errors.lastName ? "error" : ""}
                    />
                    {errors.lastName && <p className="sign-up-error">{errors.lastName}</p>}
                </div>
                <div className="sign-up-form-group">
                    <label>Phone Number<span className="asterisk">*</span></label>
                    <div className="phone-input-container">
                        <span className="country-code">+1</span>
                        <input
                            type="text"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            className={errors.phoneNumber ? "error" : ""}

                            maxLength="10"
                        />
                    </div>
                    {errors.phoneNumber && <p className="sign-up-error">{errors.phoneNumber}</p>}
                </div>
                <div className="sign-up-form-group">
                    <label>Password<span className="asterisk">*</span></label>
                    <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={errors.password ? "error" : ""}
                    />
                    {errors.password && <p className="sign-up-error">{errors.password.split("\n").map((line, index) => (
                        <React.Fragment key={index}>
                            {line}
                            <br />
                        </React.Fragment>
                    ))}</p>}
                </div>
                <div className="sign-up-form-group">
                    <label>Confirm Password<span className="asterisk">*</span></label>
                    <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className={errors.confirmPassword ? "error" : ""}
                    />
                    {errors.confirmPassword && <p className="sign-up-error">{errors.confirmPassword}</p>}
                </div>

                {/* Display API error message if present */}
                {apiErrorMessage && (
                    <div className="sign-up-api-error-message">
                        {apiErrorMessage}
                    </div>
                )}

                <button type="submit" className="sign-up-submit-btn">Sign Up</button>
            </form>
            {successMessage && <p className="sign-up-success">{successMessage}</p>}
        </div>
    );
};

export default SignupPage;